import { useId, useState } from "react";
import type { VerificationAnswer } from "@ondo/shared";

import { ANSWERS } from "./verifications.model.ts";
import type { Capacity, RequestCard } from "./verifications.model.ts";
import {
  ACTION_LABELS,
  ANSWER_COMPOSERS,
  ANSWER_LABELS,
  REFER_COMPOSER,
  VERIFICATION_COPY,
} from "./verifications.constants.ts";

type Props = {
  card: RequestCard;
  capacities: Capacity[];
  busy: boolean;
  onAnswer: (body: {
    answer: VerificationAnswer;
    body?: string | null;
    onBehalfOf?: string | null;
  }) => void;
  onRefer: (ondoId: string, note: string | null) => void;
  onAddDocument: (files: File[]) => void;
};

/** Which control has the floor. Only one composer is ever open at a time. */
type Composing = { kind: "answer"; answer: VerificationAnswer } | { kind: "refer" } | null;

export function VerificationActions({
  card,
  capacities,
  busy,
  onAnswer,
  onRefer,
  onAddDocument,
}: Props) {
  const [composing, setComposing] = useState<Composing>(null);
  const [text, setText] = useState("");
  const [ondoId, setOndoId] = useState("");
  const [onBehalfOf, setOnBehalfOf] = useState<string>("");
  const fieldId = useId();
  const capacityId = useId();
  const noteId = useId();

  if (!card.canAnswer) {
    return card.refusal ? <p className="verifications__refusal">{card.refusal}</p> : null;
  }

  const composer = composing?.kind === "answer" ? ANSWER_COMPOSERS[composing.answer] : null;

  return (
    <div className="verifications__actions">
      {composing === null && (
        <div className="verifications__buttons">
          <label className="verifications__button verifications__button--file">
            {ACTION_LABELS.addDocument}
            <input
              type="file"
              multiple
              accept="application/pdf,image/jpeg,image/png,image/tiff,image/heic,image/webp"
              onChange={(event) => {
                const files = [...(event.target.files ?? [])];
                // Cleared so choosing the same file twice fires again.
                event.target.value = "";
                if (files.length > 0) onAddDocument(files);
              }}
            />
          </label>
          <button
            type="button"
            className="verifications__button"
            onClick={() => setComposing({ kind: "refer" })}
          >
            {ACTION_LABELS.refer}
          </button>
          {ANSWERS.map((answer) => (
            <button
              key={answer}
              type="button"
              className={`verifications__button verifications__button--${answer}`}
              onClick={() => {
                setText("");
                setComposing({ kind: "answer", answer });
              }}
            >
              {ANSWER_LABELS[answer]}
            </button>
          ))}
        </div>
      )}

      {composer && composing?.kind === "answer" && (
        <div className="verifications__composer">
          <label htmlFor={fieldId}>{composer.label}</label>
          <textarea
            id={fieldId}
            rows={3}
            value={text}
            placeholder={composer.placeholder}
            onChange={(event) => setText(event.target.value)}
          />

          {/*
            Rendered only when there is something to choose. Answering as
            yourself is the ordinary case and needs no control to say so — see
            `capacitiesFor`, which returns nothing rather than one option.
          */}
          {capacities.length > 0 && (
            <>
              <label htmlFor={capacityId}>{VERIFICATION_COPY.onBehalfLabel}</label>
              <select
                id={capacityId}
                value={onBehalfOf}
                onChange={(event) => setOnBehalfOf(event.target.value)}
              >
                {capacities.map((capacity) => (
                  <option key={capacity.ondoId ?? "self"} value={capacity.ondoId ?? ""}>
                    {capacity.label}
                  </option>
                ))}
              </select>
              <p className="verifications__help">{VERIFICATION_COPY.onBehalfHelp}</p>
            </>
          )}

          <div className="verifications__buttons">
            <button
              type="button"
              className="verifications__button"
              onClick={() => setComposing(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="verifications__button verifications__button--send"
              disabled={busy || (composer.required && text.trim().length === 0)}
              onClick={() => {
                onAnswer({
                  answer: composing.answer,
                  body: text.trim() || null,
                  onBehalfOf: onBehalfOf || null,
                });
                setComposing(null);
              }}
            >
              {ANSWER_LABELS[composing.answer]}
            </button>
          </div>
        </div>
      )}

      {composing?.kind === "refer" && (
        <div className="verifications__composer">
          <label htmlFor={fieldId}>{REFER_COMPOSER.label}</label>
          <input
            id={fieldId}
            value={ondoId}
            placeholder={REFER_COMPOSER.placeholder}
            onChange={(event) => setOndoId(event.target.value)}
          />
          <p className="verifications__help">{REFER_COMPOSER.help}</p>
          <label htmlFor={noteId}>{REFER_COMPOSER.noteLabel}</label>
          <textarea
            id={noteId}
            rows={2}
            value={text}
            placeholder={REFER_COMPOSER.notePlaceholder}
            onChange={(event) => setText(event.target.value)}
          />
          <div className="verifications__buttons">
            <button
              type="button"
              className="verifications__button"
              onClick={() => setComposing(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="verifications__button verifications__button--send"
              disabled={busy || ondoId.trim().length === 0}
              onClick={() => {
                onRefer(ondoId.trim(), text.trim() || null);
                setComposing(null);
              }}
            >
              {REFER_COMPOSER.action}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
